import { useMutation, useQuery } from "@tanstack/react-query";
import { Text } from "@ui-kitten/components";
import AutoRenewalConsentSection from "components/AutoRenewalConsentSection";
import Button from "components/Button";
import LoginContainer from "components/LoginContainer";
import TextInput from "components/TextInput";
import { AUTH_SCREEN_BUTTON_BACKGROUND } from "constants/";
import { login } from "features/slices/authSlice";
import { AgreementDetails } from "features/types";
import { Field, Formik } from "formik";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { FinishSignupScreenProps } from "navigation/types";
import i18n from "plugins/localization";
import { updateSubscriptionFetcher } from "queries/account.queries";
import { resetPasswordFetcher, setupContextFetcher } from "queries/user.queries";
import React, { useEffect, useState } from "react";
import { Pressable, View } from "react-native";
import { loginInputProps } from "screens/Login/Login";
import {
  InitialValues,
  validationSchema,
} from "screens/ResetPassword/ResetPassword";
import handleHTTPError from "utils/handleHTTPError";
import { toDashboard } from "utils/navigationUtils";

const FinishSignup = ({ navigation, route }: FinishSignupScreenProps) => {
  const { encodedEmail, token } = route.params;
  const dispatch = useAppDispatch();
  const storedUserId = useAppSelector((state) => state.auth.id);
  const decodedEmail = window.atob(decodeURIComponent(encodedEmail));

  const [consentGiven, setConsentGiven] = useState(false);

  // An already-logged-in user has no signup to finish; send them to the
  // dashboard. Navigate via the screen's own `navigation` prop rather than the
  // global navigationRef — on mount the ref may not be ready yet (the container
  // marks itself ready in a parent effect that runs after this child effect).
  useEffect(() => {
    if (storedUserId) {
      navigation.navigate("AuthenticatedFlow", {
        screen: "MainFlow",
        params: {
          screen: "Home",
          params: { screen: "DashboardScreen" },
        },
      });
    }
  }, [storedUserId]);

  const initialValues: InitialValues = {
    password: "",
    confirmPassword: "",
  };

  const { mutateAsync: resetPassword, isLoading } =
    useMutation(resetPasswordFetcher);

  // Gated by the reset token. On any error we treat it as "no consent needed" —
  // token problems will surface when the password reset itself is submitted.
  const { data: setupContext } = useQuery(
    ["setupContext", encodedEmail],
    () => setupContextFetcher({ username: decodedEmail, token }),
    { retry: false, staleTime: Infinity },
  );

  const consentSubscriptions = setupContext?.autoRenewalSubscriptions ?? [];
  const consentRequired =
    !!setupContext?.autoRenewalConsentRequired &&
    consentSubscriptions.length > 0;

  // The list is fully filtered server-side. One checkbox covers every plan; the
  // modal stacks each plan's terms with the plan name as a divider.
  const combinedTermsBody = consentSubscriptions
    .map((sub) =>
      consentSubscriptions.length > 1
        ? `${sub.planPackageName}\n\n${sub.termsBody}`
        : sub.termsBody,
    )
    .join("\n\n———\n\n");

  const displayAgreements: AgreementDetails[] | undefined = consentRequired
    ? [
        {
          id: consentSubscriptions[0].agreementId,
          body: combinedTermsBody,
        } as AgreementDetails,
      ]
    : undefined;

  const planNames = consentSubscriptions
    .map((sub) => sub.planPackageName)
    .join(", ");

  const onSubmit = async (values: InitialValues) => {
    await resetPassword({
      ...values,
      username: decodedEmail,
      token,
    });
    await dispatch(
      login({ username: decodedEmail, password: values.password }),
    );

    // Record consent + enable auto-renewal for each subscription. The password
    // is already set and the account is live, so a failure here must not block
    // the member — they can opt in later from Manage Subscriptions.
    if (consentRequired && consentGiven) {
      for (const sub of consentSubscriptions) {
        try {
          await updateSubscriptionFetcher({
            accountId: sub.accountId,
            subscriptionId: sub.subscriptionId,
            autoRenew: true,
            autoRenewalAgreementId: sub.agreementId,
          });
        } catch (error) {
          handleHTTPError(error);
        }
      }
    }

    toDashboard();
  };

  return (
    <LoginContainer title={i18n.t("screens.FinishSignup.title")}>
      <View style={{ padding: 15 }}>
        <Text style={{ marginVertical: 15 }} status="control">
          {i18n.t("screens.FinishSignup.heading")}
        </Text>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ isValid, handleSubmit }) => (
            <View>
              <View style={{ marginVertical: 10 }}></View>
              <Field
                component={TextInput}
                name="password"
                appearance="default"
                secureTextEntry
                label={
                  <Text status="control">{i18n.t("labels.password")}</Text>
                }
                {...loginInputProps}
              ></Field>
              <View style={{ marginVertical: 10 }}></View>

              <Field
                component={TextInput}
                name="confirmPassword"
                appearance="default"
                secureTextEntry
                label={
                  <Text status="control">
                    {i18n.t("labels.confirmPassword")}
                  </Text>
                }
                {...loginInputProps}
              ></Field>
              <View style={{ marginVertical: 10 }}></View>

              {consentRequired && (
                <View style={{ marginBottom: 10 }}>
                  <Text
                    status="control"
                    category="s1"
                    style={{ marginBottom: 4 }}
                  >
                    {i18n.t("screens.FinishSignup.autoRenewalHeading")}
                  </Text>
                  <Text status="control" style={{ marginBottom: 8 }}>
                    {i18n.t("screens.FinishSignup.autoRenewalAppliesTo", {
                      plans: planNames,
                    })}
                  </Text>
                  <AutoRenewalConsentSection
                    agreements={displayAgreements}
                    consentGiven={consentGiven}
                    onChange={setConsentGiven}
                    onDark
                  />
                </View>
              )}

              <Button
                style={{
                  marginVertical: 10,
                  backgroundColor: AUTH_SCREEN_BUTTON_BACKGROUND,
                  borderWidth: 0,
                }}
                disabled={!isValid || (consentRequired && !consentGiven)}
                onPress={() => handleSubmit()}
                loading={isLoading}
              >
                {i18n.t("common.submit")}
              </Button>
              <Pressable
                onPress={() =>
                  navigation.navigate("LoginFlow", { screen: "Login" })
                }
              >
                <Text style={{ color: "white", textAlign: "center" }}>
                  {i18n.t("common.logIn")}
                </Text>
              </Pressable>
            </View>
          )}
        </Formik>
      </View>
    </LoginContainer>
  );
};

export default FinishSignup;
